import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAdminImage } from '@/hooks/useAdminImage';

const DEFAULT_SERVICE_IMAGES: Record<string, string> = {
  dental: 'https://readdy.ai/api/search-image?query=Close%20up%20portrait%20of%20beautiful%20woman%20with%20perfect%20bright%20white%20smile%20and%20flawless%20teeth%2C%20professional%20dental%20beauty%20photography%2C%20soft%20warm%20studio%20lighting%2C%20warm%20skin%20tones%2C%20clean%20light%20background%2C%20natural%20radiant%20look%2C%20high%20end%20cosmetic%20dentistry%20result%2C%20editorial%20style%2C%20ultra%20sharp%20detail&width=800&height=600&seq=service-card-dental-2026&orientation=landscape',
  surgery: 'https://readdy.ai/api/search-image?query=Elegant%20beautiful%20woman%20with%20refined%20facial%20features%2C%20natural%20flawless%20glowing%20skin%2C%20soft%20warm%20studio%20lighting%2C%20luxury%20beauty%20photography%2C%20graceful%20expression%2C%20premium%20aesthetic%20medicine%20result%2C%20sophisticated%20editorial%20portrait%2C%20cream%20and%20warm%20neutral%20background%2C%20subtle%20anti%20aging%20beauty%20look&width=800&height=600&seq=service-card-surgery-2026&orientation=landscape',
  hair: 'https://readdy.ai/api/search-image?query=Handsome%20confident%20man%20with%20full%20thick%20natural%20looking%20dark%20hair%2C%20well%20groomed%20professional%20appearance%2C%20soft%20warm%20studio%20lighting%2C%20clean%20neutral%20background%2C%20natural%20dense%20hairline%20close%20up%2C%20premium%20grooming%20editorial%20portrait%2C%20sharp%20focus%2C%20ultra%20realistic&width=800&height=600&seq=service-card-hair-2026&orientation=landscape',
};

const ADMIN_IMAGE_KEYS: Record<string, string> = {
  dental: 'images.home.services_dental_img',
  surgery: 'images.home.services_botox_img',
  hair: 'images.home.services_hair_img',
};

const services = [
  { key: 'dental', icon: 'ri-tooth-line' },
  { key: 'surgery', icon: 'ri-heart-pulse-line' },
  { key: 'hair', icon: 'ri-user-smile-line' },
];

function ServiceCard({ serviceKey, icon }: { serviceKey: string; icon: string }) {
  const { t } = useTranslation();
  const imageUrl = useAdminImage('home', ADMIN_IMAGE_KEYS[serviceKey], DEFAULT_SERVICE_IMAGES[serviceKey]);

  return (
    <Link
      to={`/pricing#${serviceKey}`}
      className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-500 cursor-pointer no-underline flex flex-col"
    >
      <div className="relative h-52 md:h-60 overflow-hidden">
        <img
          src={imageUrl}
          alt={t(`services.${serviceKey}.title`)}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"></div>
        <div className="absolute bottom-4 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/20 group-hover:bg-primary-500/80 group-hover:border-primary-400 transition-all duration-500">
          <i className={`${icon} text-lg text-white`}></i>
        </div>
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
          {t(`services.${serviceKey}.title`)}
        </h3>
        <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed font-body flex-1">
          {t(`services.${serviceKey}.desc`)}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-xs font-label text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
          {t(`services.${serviceKey}.count`)}
          <span className="text-foreground-300">·</span>
          {t('gallery.view_details')}
          <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );
}

export default function ServicesSection() {
  const { t } = useTranslation();

  return (
    <section id="services" className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 md:mb-18">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-label">
              {t('services.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mt-3 leading-tight">
              {t('services.heading')}
            </h2>
          </div>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label tracking-wide transition-colors cursor-pointer whitespace-nowrap no-underline"
          >
            {t('services.cta')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {services.map((service) => (
            <ServiceCard key={service.key} serviceKey={service.key} icon={service.icon} />
          ))}
        </div>
      </div>
    </section>
  );
}